import { Tree } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styles from './MainAddressSection.module.scss';
import { getOptionByValueMap, getOptionText, getPreviewKey } from './utils';

const { TreeNode } = Tree;

const PreviewTitle = ({ children, onClick }) => {
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onClick(event);
  };

  return (
    <div
      className={styles.serviceSetPreviewToggle}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};

export const AddressPreview = ({
  allCompanyAddresses,
  cityIds = [],
  cityOptions = [],
  cityRegionIds = [],
  cityRegionOptions = [],
  houseIds = [],
  houseOptions = [],
  streetIds = [],
  streetOptions = [],
}) => {
  const selectedCities = useMemo(() => {
    const cityOptionsMap = getOptionByValueMap(cityOptions);

    return cityIds
      .map((cityId) => cityOptionsMap.get(String(cityId)))
      .filter(Boolean);
  }, [cityIds, cityOptions]);
  const selectedStreets = useMemo(() => {
    const streetOptionsMap = getOptionByValueMap(streetOptions);

    return streetIds
      .map((streetId) => streetOptionsMap.get(String(streetId)))
      .filter(Boolean);
  }, [streetIds, streetOptions]);
  const selectedCityRegions = useMemo(() => {
    const cityRegionOptionsMap = getOptionByValueMap(cityRegionOptions);

    return cityRegionIds.map(
      (cityRegionId) =>
        cityRegionOptionsMap.get(String(cityRegionId)) || {
          value: cityRegionId,
          text: String(cityRegionId),
        }
    );
  }, [cityRegionIds, cityRegionOptions]);
  const selectedHousesByStreetKey = useMemo(() => {
    const houseOptionsMap = getOptionByValueMap(houseOptions);
    const map = new Map();

    houseIds.forEach((houseId) => {
      const houseOption = houseOptionsMap.get(String(houseId));

      if (!houseOption) {
        return;
      }

      const streetKey = String(houseOption.streetId ?? '');
      const houses = map.get(streetKey) || [];

      map.set(streetKey, [...houses, houseOption]);
    });

    return map;
  }, [houseIds, houseOptions]);
  const housesByStreetKey = useMemo(() => {
    const map = new Map();

    houseOptions.forEach((houseOption) => {
      const streetKey = String(houseOption.streetId ?? '');
      const houses = map.get(streetKey) || [];

      map.set(streetKey, [...houses, houseOption]);
    });

    return map;
  }, [houseOptions]);
  const initialExpandedKeys = useMemo(() => {
    if (!selectedCities.length) {
      return [];
    }

    return selectedCities.flatMap((cityOption, cityIndex) => {
      const cityKey = getPreviewKey(
        'address-city',
        cityOption.value,
        cityIndex
      );
      const streetKeys = selectedStreets.map((streetOption, streetIndex) =>
        getPreviewKey(`${cityKey}-street`, streetOption.value, streetIndex)
      );

      return [cityKey, ...streetKeys];
    });
  }, [selectedCities, selectedStreets]);
  const [expandedKeys, setExpandedKeys] = useState(initialExpandedKeys);

  useEffect(() => {
    setExpandedKeys(initialExpandedKeys);
  }, [initialExpandedKeys]);

  const togglePreviewNode = useCallback((nodeKey) => {
    setExpandedKeys((prevKeys) =>
      prevKeys.includes(nodeKey)
        ? prevKeys.filter((key) => key !== nodeKey)
        : [...prevKeys, nodeKey]
    );
  }, []);

  const handleTitleClick = useCallback(
    (event, nodeKey) => {
      event.stopPropagation();
      togglePreviewNode(nodeKey);
    },
    [togglePreviewNode]
  );

  return (
    <div className={styles.serviceSetInlinePreview}>
      {allCompanyAddresses ? (
        <div className={styles.serviceSetPreviewEmpty}>Все адреса УК</div>
      ) : !selectedCities.length ? (
        <div className={styles.serviceSetPreviewEmpty}>Выберите город</div>
      ) : (
        <div className={styles.serviceSetPreviewScroll}>
          <Tree
            className={styles.serviceSetPreviewTree}
            expandedKeys={expandedKeys}
            selectable={false}
            onExpand={setExpandedKeys}
          >
            {selectedCities.map((cityOption, cityIndex) => {
              const cityKey = getPreviewKey(
                'address-city',
                cityOption.value,
                cityIndex
              );

              return (
                <TreeNode
                  key={cityKey}
                  title={
                    <PreviewTitle
                      onClick={(event) => handleTitleClick(event, cityKey)}
                    >
                      <div className={styles.serviceSetPreviewTitle}>
                        {getOptionText(cityOption)}
                      </div>
                    </PreviewTitle>
                  }
                >
                  {selectedCityRegions.map((cityRegionOption, regionIndex) => (
                    <TreeNode
                      key={getPreviewKey(
                        `${cityKey}-region`,
                        cityRegionOption.value,
                        regionIndex
                      )}
                      title={
                        <div className={styles.serviceSetPreviewNode}>
                          Микрорайон: {getOptionText(cityRegionOption)} — все
                          дома
                        </div>
                      }
                    />
                  ))}
                  {selectedStreets.length ? (
                    selectedStreets.map((streetOption, streetIndex) => {
                      const streetKey = getPreviewKey(
                        `${cityKey}-street`,
                        streetOption.value,
                        streetIndex
                      );
                      const streetHouses =
                        selectedHousesByStreetKey.get(
                          String(streetOption.value)
                        ) || [];
                      const streetHouseValueSet = new Set(
                        streetHouses.map((houseOption) =>
                          String(houseOption.value)
                        )
                      );
                      const allStreetHouses =
                        housesByStreetKey.get(String(streetOption.value)) || [];
                      const hasAllStreetHousesSelected =
                        Boolean(allStreetHouses.length) &&
                        allStreetHouses.every((houseOption) =>
                          streetHouseValueSet.has(String(houseOption.value))
                        );

                      return (
                        <TreeNode
                          key={streetKey}
                          title={
                            <PreviewTitle
                              onClick={(event) =>
                                handleTitleClick(event, streetKey)
                              }
                            >
                              <div className={styles.serviceSetPreviewNode}>
                                {getOptionText(streetOption)}
                              </div>
                            </PreviewTitle>
                          }
                        >
                          {hasAllStreetHousesSelected ? (
                            <TreeNode
                              key={`${streetKey}-all-houses`}
                              title={
                                <div className={styles.serviceSetPreviewReason}>
                                  Все дома
                                </div>
                              }
                            />
                          ) : streetHouses.length ? (
                            streetHouses.map((houseOption, houseIndex) => (
                              <TreeNode
                                key={getPreviewKey(
                                  `${streetKey}-house`,
                                  houseOption.value,
                                  houseIndex
                                )}
                                title={
                                  <div
                                    className={styles.serviceSetPreviewReason}
                                  >
                                    {getOptionText(houseOption)}
                                  </div>
                                }
                              />
                            ))
                          ) : (
                            <TreeNode
                              disabled
                              key={`${streetKey}-empty`}
                              title={
                                <div className={styles.serviceSetPreviewEmpty}>
                                  Дома не выбраны
                                </div>
                              }
                            />
                          )}
                        </TreeNode>
                      );
                    })
                  ) : !selectedCityRegions.length ? (
                    <TreeNode
                      disabled
                      key={`${cityKey}-empty`}
                      title={
                        <div className={styles.serviceSetPreviewEmpty}>
                          Улицы не выбраны
                        </div>
                      }
                    />
                  ) : null}
                </TreeNode>
              );
            })}
          </Tree>
        </div>
      )}
    </div>
  );
};
